import {
  Bike,
  Check,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Hand,
  HelpCircle,
  PackageCheck,
  Truck,
  XCircle,
} from 'lucide-react';
import { deliveryLabel, type DeliveryDetail, type DeliveryRow } from '@/lib/deliveries';

const stageAppearance: Record<string, { color: string; icon: typeof Truck }> = {
  draft: { color: 'border-border bg-muted/50 text-muted-foreground', icon: CircleDashed },
  booked: { color: 'border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300', icon: Clock3 },
  packed: {
    color: 'border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-300',
    icon: PackageCheck,
  },
  dispatched: {
    color: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
    icon: Truck,
  },
  in_transit: {
    color: 'border-cyan-500/20 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300',
    icon: Truck,
  },
  out_for_delivery: {
    color: 'border-amber-500/20 bg-amber-500/10 text-amber-800 dark:text-amber-300',
    icon: Truck,
  },
  delivered: {
    color: 'border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    icon: CheckCircle2,
  },
  cancelled: {
    color: 'border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300',
    icon: XCircle,
  },
};
export function DeliveryBadge({ stage }: { stage: string | null }) {
  const appearance = stageAppearance[stage || ''] || stageAppearance.draft!;
  const Icon = appearance.icon;
  return (
    <span
      className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${appearance.color}`}
    >
      <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      {deliveryLabel(stage)}
    </span>
  );
}
export function DeliveryMethod({ method }: { method: DeliveryRow['fulfillment_method'] }) {
  const Icon =
    method === 'local_delivery'
      ? Bike
      : method === 'courier'
        ? Truck
        : method === 'pickup'
          ? Hand
          : HelpCircle;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <Icon aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
      {method === 'unspecified' ? 'Method not set' : deliveryLabel(method)}
    </span>
  );
}
export function deliveryNextStep(row: DeliveryRow): string {
  if (['returned', 'partially_returned'].includes(row.sale_status))
    return 'Manage the customer return from the original sale.';
  if (!row.stage) return 'Choose a delivery method to start preparing this order.';
  if (row.packing_requires_review) return 'Recheck the items and recipient before dispatching.';
  if (row.issue) return 'Review the delivery issue and record the next update.';
  switch (row.stage) {
    case 'draft':
      return 'Book or confirm the sale before preparing the parcel.';
    case 'booked':
      return 'Check the items and address, then mark this parcel packed.';
    case 'packed':
      return row.fulfillment_method === 'courier'
        ? 'Add the courier and tracking number when you dispatch.'
        : 'Hand the parcel to your delivery team or complete the handover.';
    case 'dispatched':
    case 'in_transit':
    case 'out_for_delivery':
      return 'Record the next delivery update and keep the customer informed.';
    case 'delivered':
      return 'Delivery complete. Your shipment history and documents are available below.';
    case 'cancelled':
      return 'This shipment is cancelled. Its original sale and history are retained.';
  }
}
export function ShipmentProgress({ row }: { row: DeliveryDetail }) {
  if (!row.stage || row.stage === 'draft' || row.stage === 'cancelled') return null;
  const current = ['in_transit', 'out_for_delivery'].includes(row.stage) ? 'dispatched' : row.stage;
  return (
    <div className="mt-6 border-t border-border/70 pt-5">
      <ol aria-label="Shipment milestones" className="grid grid-cols-4 gap-2">
        {(['booked', 'packed', 'dispatched', 'delivered'] as const).map((step, index) => {
          const active = current === step;
          const recorded = row.events.some(
            (event) =>
              event.to_stage === step && event.occurred_at && event.action === 'status_change',
          );
          return (
            <li key={step} className="relative min-w-0 text-center">
              <div
                className={`relative z-[1] mx-auto flex h-9 w-9 items-center justify-center rounded-full border text-xs font-semibold ${active ? 'border-primary bg-primary text-primary-foreground ring-4 ring-primary/10' : recorded ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'border-border bg-card text-muted-foreground'}`}
              >
                {recorded && !active ? <Check className="h-4 w-4" aria-hidden="true" /> : index + 1}
              </div>
              {index < 3 && (
                <span
                  aria-hidden="true"
                  className="absolute left-[65%] right-[-35%] top-[18px] h-px bg-border"
                />
              )}
              <p
                className={`mt-2 break-words text-[11px] sm:text-xs ${active ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
              >
                {active ? deliveryLabel(row.stage) : deliveryLabel(step)}
              </p>
              <p className="mt-0.5 text-[10px] text-muted-foreground">
                {active ? 'Current stage' : recorded ? 'Recorded' : 'Not recorded'}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
